// src/features/inventory/pages/BatchListPage.jsx
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  Search as SearchIcon,
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { useLotFiltering } from '../hooks/useLotFiltering';
import { useInventoryLotPermissions } from '../../auth/hooks/usePermissions';
import { LOT_STATUS_COLOR, LOT_STATUSES } from '../constants/lotStatuses';

import PageHeader from '../../../components/common/PageHeader';
import { withInfo } from '../../../components/common/InfoTip';
import QrCode2Icon from '@mui/icons-material/QrCode2';

const STATUS_OPTIONS = ['all', ...LOT_STATUSES];

export default function BatchListPage() {
  const { t } = useTranslation(['inventory', 'common']);
  const navigate = useNavigate();
  const { canRead } = useInventoryLotPermissions();

  const {
    lots,
    loading,
    error,
    clearError,
    searchTerm,
    statusFilter,
    hasActiveFilters,
    setSearchTerm,
    setStatusFilter,
    clearFilters,
  } = useLotFiltering();

  const columns = useMemo(() => [
    {
      field: 'lot_number',
      headerName: t('inventory:batches.grid.lot_number'),
      flex: 1.2,
      minWidth: 140,
      renderCell: ({ value }) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
          {value}
        </Typography>
      ),
    },
    {
      field: 'item',
      headerName: t('inventory:batches.grid.item'),
      flex: 1.5,
      minWidth: 160,
      valueGetter: (value) => value?.name ?? '—',
      renderCell: ({ value }) => (
        <Typography variant="body2">{value}</Typography>
      ),
    },
    {
      field: 'item_sku',
      headerName: t('inventory:batches.grid.item_sku'),
      flex: 0.9,
      minWidth: 100,
      valueGetter: (_, row) => row.item?.sku ?? '—',
      renderCell: ({ value }) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace' }} color="text.secondary">
          {value}
        </Typography>
      ),
    },
    withInfo({
      field: 'quantity_on_hand',
      headerName: t('inventory:batches.grid.qty_on_hand'),
      flex: 0.8,
      minWidth: 100,
      renderCell: ({ value }) => (
        <Typography variant="body2" fontWeight="medium">{value}</Typography>
      ),
    }, t('inventory:help.batches.total')),
    withInfo({
      field: 'status',
      headerName: t('inventory:batches.grid.status'),
      flex: 0.9,
      minWidth: 110,
      renderCell: ({ value }) => (
        <Chip
          label={t(`inventory:batches.filters.${value?.toLowerCase()}`)}
          color={LOT_STATUS_COLOR[value?.toLowerCase()] || 'default'}
          size="small"
          variant="outlined"
        />
      ),
    }, t('inventory:help.batches.status')),
    withInfo({
      field: 'expiry_date',
      headerName: t('inventory:batches.grid.expiry_date'),
      flex: 1,
      minWidth: 120,
      renderCell: ({ value }) => value
        ? <Typography variant="body2">{new Date(value).toLocaleDateString()}</Typography>
        : <Typography variant="body2" color="text.secondary">—</Typography>,
    }, t('inventory:help.batches.expiry_date')),
  ], [t]);

  if (!canRead) return null;

  return (
    <Box flex={1} minHeight={0} display="flex" flexDirection="column">
      <PageHeader
        icon={QrCode2Icon}
        title={t('inventory:batches.title')}
        subtitle={t('inventory:batches.subtitle')}
      />

      <Box mb={2} display="flex" gap={1.5} alignItems="center" flexWrap="wrap">
        <TextField
          size="small"
          placeholder={t('inventory:batches.filters.search_placeholder')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          slotProps={{ input: { startAdornment: <SearchIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} /> } }}
          sx={{ flex: 1, minWidth: "200px" }}
        />
        <ToggleButtonGroup
          size="small"
          value={statusFilter}
          exclusive
          onChange={(_, val) => { if (val !== null) setStatusFilter(val); }}
        >
          {STATUS_OPTIONS.map((s) => (
            <ToggleButton key={s} value={s}>
              {t(`inventory:batches.filters.${s === 'all' ? 'all_statuses' : s}`)}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        {hasActiveFilters && (
          <Button size="small" onClick={clearFilters}>
            {t('inventory:items.filter.clear_filters')}
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={clearError}>{error}</Alert>
      )}

      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <DataGrid
          rows={lots}
          columns={columns}
          getRowId={(row) => row.lot_id}
          loading={loading}
          onRowClick={(params) => navigate(`${params.row.lot_id}`)}
          pageSizeOptions={[25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          disableColumnFilter
          disableColumnMenu
          disableColumnResize
          disableRowSelectionOnClick
          sx={{
            flex: 1,
            border: 'none',
            '& .MuiDataGrid-row': { cursor: 'pointer' },
            '& .MuiDataGrid-cell': { alignItems: 'center' },
          }}
        />
      </Box>
    </Box>
  );
}
